import { useState } from 'react';
import { CalendarPlus } from 'lucide-react';
import { ComplianceRefusal } from '../api/conversations';
import { eventsApi, ValidationFailure } from '../api/events';
import type { FieldErrors } from '../api/events';
import { useEventTypes } from '../hooks/useEventTypes';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { RefusalCard } from '../ui/ComplianceNotice';
import {
  emptyEventForm,
  eventFormPayload,
  allRequired,
  EventFormFields,
  EventRequirementFields,
  incompleteReason,
  normaliseFieldErrors,
} from './EventForm';
import type { EventFormValues, EventRequires } from './EventForm';
import { RepeatFields, repeatIncompleteReason, repeatPayload } from './EventRepeat';
import type { RepeatChoice } from './EventRepeat';
import { prefillFrom } from './eventTypePrefill';
import type { PrefilledFields } from './eventTypePrefill';

type ThreadAudience = 'Everyone' | 'AdultsOnly';

const AUDIENCES: { value: ThreadAudience; label: string; detail: string }[] = [
  {
    value: 'Everyone',
    label: 'Everyone in the unit',
    detail: 'Youth included, so two adults are required and unit leaders monitor it automatically.',
  },
  {
    value: 'AdultsOnly',
    label: 'Adults only',
    detail: 'Leaders and parents. No youth in the thread, so the two-adult rule does not apply.',
  },
];

/**
 * Creating an event. The fields themselves live in `EventForm`, shared with the edit screen; what
 * belongs only here is the thread audience, which is decided once and never again — changing who
 * is in a conversation afterwards is a participant decision with its own rules, not a correction
 * to a fact about the event.
 */
export function NewEventScreen({
  unitId,
  unitName,
  unitTimeZoneId,
  allowsYouthParticipants,
  onCreated,
  onCancel,
}: {
  unitId: string;
  unitName: string;
  unitTimeZoneId: string;
  /**
   * False in a Pack, where the roster's youth are never conversation participants. There is then
   * no audience to choose between — the whole-unit thread is the parents-and-leaders one — so the
   * picker is not rendered at all rather than rendered disabled. A disabled control has to say
   * why; a control with one option was never a choice.
   */
  allowsYouthParticipants: boolean;
  onCreated: (eventId: string) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<EventFormValues>(emptyEventForm);
  // Seeded from the unit, and the server resolves it the same way from the same flag. A Pack that
  // was handed 'Everyone' here refused the whole creation, which is what made its calendar
  // unusable (TROOP-37).
  const [audience, setAudience] = useState<ThreadAudience>(
    allowsYouthParticipants ? 'Everyone' : 'AdultsOnly',
  );
  const eventTypes = useEventTypes(unitId);

  // The repeat, and the date it stops on. Held here rather than in the shared form fields for the
  // same reason the audience is: it is a fact about this act of creating, not about the event.
  // What it produces is ordinary events, each of which is edited on its own afterwards.
  const [repeat, setRepeat] = useState<RepeatChoice>(null);
  const [repeatUntil, setRepeatUntil] = useState('');

  // Which sections this event carries. All four on until a kind says otherwise, which is what
  // every event did before they existed.
  const [requires, setRequires] = useState<EventRequires>(allRequired);

  // Which boxes the chosen kind filled. Kept so the form can say so under each one, and so that
  // switching kinds may replace what the last kind put there without touching what a person typed.
  const [prefilled, setPrefilled] = useState<PrefilledFields>({});

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [refusal, setRefusal] = useState<{ title: string; detail: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function change(patch: Partial<EventFormValues>) {
    setValues((v) => ({ ...v, ...patch }));

    // Anything the leader touches stops being the kind's. The note under the box goes with it, and
    // so does this kind's claim on the box if they later switch to a different one.
    setPrefilled((current) => {
      const next = { ...current };
      for (const field of Object.keys(patch) as (keyof EventFormValues)[]) {
        delete next[field];
      }
      return next;
    });
  }

  /**
   * Picking a kind fills the boxes it has an answer for.
   *
   * Client-side, from the payload the picker already loaded, so switching kinds is instant and
   * needs no round trip. What lands in the boxes is ordinary form state from that moment on:
   * nothing is read back through the type, and editing the type next month changes none of it.
   */
  function chooseType(eventTypeId: string) {
    const chosen = eventTypes.find((t) => t.id === eventTypeId);
    const fill = prefillFrom(chosen, values, prefilled);

    // A box the previous kind filled and this one has no answer for is emptied with it. A
    // campground address left sitting under "Troop Meeting" is worse than an empty box.
    const cleared: Partial<EventFormValues> = {};
    for (const field of Object.keys(prefilled) as (keyof EventFormValues)[]) {
      if (!fill.prefilled[field]) cleared[field] = '';
    }

    setValues((v) => ({ ...v, ...cleared, ...fill.values, eventTypeId }));
    setPrefilled(fill.prefilled);

    // The flags follow the kind wholesale rather than through prefillFrom's "is this box empty"
    // test, which cannot tell a deliberate false from an unfilled field. A kind that says nothing
    // puts them all back on, so switching from "Troop Meeting" to "Campout" restores travel
    // rather than leaving it off because the previous kind had turned it off.
    setRequires({
      rsvp: chosen?.defaults.requiresRsvp ?? true,
      forms: chosen?.defaults.requiresForms ?? true,
      packing: chosen?.defaults.requiresPacking ?? true,
      travel: chosen?.defaults.requiresTravel ?? true,
      program: chosen?.defaults.requiresProgram ?? true,
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFieldErrors({});
    setRefusal(null);
    setError(null);

    try {
      const created = await eventsApi.create(unitId, {
        ...eventFormPayload(values, requires),
        threadAudience: audience,
        repeat: repeatPayload(repeat, repeatUntil),
      });

      // The first occurrence, which is the one the leader typed. The rest are on the calendar as
      // events in their own right; there is no series screen to send anyone to, deliberately.
      onCreated(created.eventId);
    } catch (e) {
      if (e instanceof ValidationFailure) {
        setFieldErrors(normaliseFieldErrors(e.errors));
      } else if (e instanceof ComplianceRefusal) {
        setRefusal({ title: e.title, detail: e.message });
      } else {
        setError(e instanceof Error ? e.message : 'Could not create the event.');
      }
      setBusy(false);
    }
  }

  return (
    <Page>
      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p
              className="t-eyebrow inline-flex items-center gap-2"
              style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}
            >
              <CalendarPlus size={14} aria-hidden="true" />
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 0' }}
            >
              New event
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0', maxWidth: '46ch' }}
            >
              The facts go here, as fields. A thread is created with the event, below them.
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <form onSubmit={submit} style={{ paddingBlock: 'var(--space-6)', maxWidth: '40rem' }}>
            <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
              <EventFormFields
                values={values}
                onChange={change}
                fieldErrors={fieldErrors}
                eventTypes={eventTypes}
                prefilled={prefilled}
                onChooseType={chooseType}
                zoneNote={`Times below are ${unitTimeZoneId.replace(/_/g, ' ')} — ${unitName}’s time zone, whatever zone you are in right now. Set in quarter hours.`}
              />

              <EventRequirementFields
                requires={requires}
                onChange={(patch) => setRequires((r) => ({ ...r, ...patch }))}
              />

              <RepeatFields
                unitId={unitId}
                startsAt={values.startsAt}
                choice={repeat}
                onChoose={(choice, until) => {
                  setRepeat(choice);
                  setRepeatUntil(choice ? until : '');
                }}
                until={repeatUntil}
                onUntil={setRepeatUntil}
                fieldErrors={fieldErrors}
              />

              <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                <legend className="t-label" style={{ color: 'var(--color-text-muted)' }}>
                  Who is in the thread
                </legend>

                {!allowsYouthParticipants && (
                  <p
                    className="t-body"
                    style={{
                      color: 'var(--color-text-secondary)',
                      marginTop: 'var(--space-2)',
                    }}
                  >
                    Leaders and parents. {unitName} has no youth in its conversations, so this is
                    the whole unit&rsquo;s thread rather than a narrower one.
                  </p>
                )}

                <div
                  className="flex flex-col"
                  style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}
                >
                  {(allowsYouthParticipants ? AUDIENCES : []).map((option) => (
                    <label
                      key={option.value}
                      className="flex items-start gap-3"
                      style={{
                        background: 'var(--color-surface-raised)',
                        border:
                          audience === option.value
                            ? '1.5px solid var(--color-brand-ink)'
                            : '1.5px solid var(--color-border-subtle)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--space-3) var(--space-4)',
                        minHeight: 'var(--tap-min)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="threadAudience"
                        value={option.value}
                        checked={audience === option.value}
                        onChange={() => setAudience(option.value)}
                        style={{ marginTop: '4px' }}
                      />
                      <span>
                        <span
                          className="t-body-strong"
                          style={{ color: 'var(--color-text-primary)', display: 'block' }}
                        >
                          {option.label}
                        </span>
                        <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                          {option.detail}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {refusal && (
                <RefusalCard
                  headline={refusal.title}
                  explanation={refusal.detail}
                  fixLabel="Change the thread audience"
                  onFix={() => {
                    setRefusal(null);
                    setAudience('AdultsOnly');
                  }}
                />
              )}

              {error && (
                <p
                  className="t-body"
                  role="alert"
                  style={{
                    background: 'var(--color-status-blocked-surface)',
                    border: '1px solid var(--color-status-blocked)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3)',
                    color: 'var(--color-text-primary)',
                    margin: 0,
                  }}
                >
                  {error}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  type="submit"
                  disabledReason={
                    incompleteReason(values) ?? repeatIncompleteReason(repeat, repeatUntil)
                  }
                >
                  {busy ? 'Creating…' : repeat ? 'Create events' : 'Create event'}
                </Button>

                <Button variant="secondary" onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}
